import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  Grid,
  InputAdornment,
  Stack,
  useMediaQuery,
  useTheme,
  TextField as MuiTextField
} from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import { App, ColorPicker, Skeleton, Typography } from "antd";
import FileField from "~/components/Extras/FileField";
import SelectField from "~/components/Extras/SelectField";
import TextField from "~/components/Extras/TextField";
import GenericForm from "~/components/GenericForm";
import GenericFormContainer from "~/components/GenericFormContainer";
import MDBox from "~/components/MDBox";
import MDButton from "~/components/MDButton";
import { roles } from "~/config";
import useAuthorization from "~/hooks/useAuthorization";
import useImageCompression from "~/hooks/useImageCompression";
import {
  useCreatePlantMutation,
  useUpdatePlantMutation,
} from "~/queries/plantMasterQuery";
import { getPlantCategory } from "~/services/plant.service";
import { extractFileName, getImageUrl } from "~/utils/utils";
import {
  MAX_CHARACTER_LENGTH,
  formInitialValues,
  formValidationSchema,
} from "./helper";
import Color from 'color';
import namer from 'color-namer';


const CreatePlant = () => {
  // Material Ui Hooks
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));

  const { message: antMessage } = App.useApp();
  // Redux hooks
  const dispatch = useDispatch();
  // React hooks
  const [fileList, setFileList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [submitButtonLabel, setSubmitButtonLabel] = useState("Submit");
  const [cardHeaderTitle, setCardHeaderTitle] = useState("Create Plant");
  const [colorName, setColorName] = useState('');
  const [primary, setPrimary] = useState('#1677ff');
  const [description, setDescription] = useState("");

  const formRef = useRef(null);
  const { compressedFile, isCompressing } = useImageCompression(
    fileList[0]?.originFileObj,
    "Plant_Img",
    formRef
  );
  const { userHasRequiredRole } = useAuthorization([roles.ADMIN]);

  // Router Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // query mutation hooks
  const createPlantMutation = useCreatePlantMutation();
  const updatePlantMutation = useUpdatePlantMutation();

  const isPlantationUpdated = useMemo(
    () => location.state?.plantation ?? false,
    [location]
  );

  const getReadableColor = (hex) => {
    const names = namer(hex).pantone;
    setColorName(names.length > 0 ? names[0].name : 'Unknown');
    return names.length > 0 ? names[0].name : 'Unknown';
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ["Plant Category", 1],
        queryFn: () => getPlantCategory("Plant Habit"),
      },
      {
        queryKey: ["Plant Category", 2],
        queryFn: () => getPlantCategory("Plant Nature"),
      },
      {
        queryKey: ["Plant Category", 3],
        queryFn: () => getPlantCategory("Plantation Type"),
      },
      {
        queryKey: ["Plant Category", 4],
        queryFn: () => getPlantCategory("Plant Season"),
      },
      {
        queryKey: ["Plant Category", 5],
        queryFn: () => getPlantCategory("Plant Uses"),
      },
      {
        queryKey: ["Plant Category", 6],
        queryFn: () => getPlantCategory("Frequency"),
      },
    ],
  });

  const handleSubmit = async (values, helpers) => {
    console.log(values, "values")
    if (location.state && params) {
      // console.log("During the update", params);
      try {
        const updateResponse = await updatePlantMutation.mutateAsync({
          ...values,
          params: location.state?.Plant_Auto_ID,
          Plant_Img: values?.Plant_Img ?? false,
          Plant_Color: primary,
          Plant_Color_Name: colorName
        });

        antMessage.success(updateResponse?.message);
        navigate("/plant-master");
        helpers.resetForm();
      } catch (error) {
        // helpers.resetForm();
        setFileList([]);
        antMessage.error(error?.response?.data?.message);
      }
      // navigate(-1);
    } else {
      try {
        const response = await createPlantMutation.mutateAsync({
          ...values,
          Plant_Color: primary,
          Plant_Color_Name: colorName
        });
        helpers.resetForm();
        antMessage.success(response.message);
        navigate("/plant-master");
      } catch (error) {
        console.log(error);
        setFileList([]);
        antMessage.error(error?.response?.data?.message);
        // helpers.resetForm();
      }
    }
  };

  const handleChange = (info) => {
    let newFileList = [...info.fileList];
    console.log("INFO", info);
    // 1. Limit the number of uploaded files
    // Only to show two recent uploaded files, and old ones will be replaced by the new
    newFileList = newFileList.slice(-2);

    // 2. Read from response and show file link
    newFileList = newFileList.map((file) => {
      if (file.response) {
        // Component will show file.url as link
        file.url = file.response.url;
      }
      return file;
    });
    console.log(newFileList);
    formRef.current.setFieldValue("Plant_Img", newFileList[0].originFileObj);
    setFileList(newFileList);

    if (info.file.status !== "uploading") {
      console.log(info.file, info.fileList);
    }
    if (info.file.status === "done") {
      // message.success(`${info.file.name} file uploaded successfully`);
    }
    if (info.file.status === "removed") {
      console.log(info.file);
      setFileList([]);
    } else if (info.file.status === "error") {
      // message.error(`${info.file.name} file upload failed.`);
    }
  };

  useEffect(() => {
    if (location.state) {
      setSubmitButtonLabel("Update");
      setCardHeaderTitle("Update Plant");
      setPrimary(location.state?.Plant_Color);
      setColorName(location.state?.Plant_Color_Name);
      if (location.state?.Plant_Img) {
        setFileList([
          {
            uid: 0,
            name: extractFileName(location.state.Plant_Img),
            status: "done",
            url: getImageUrl(location.state.Plant_Img),
          },
        ]);
      } else {
        setFileList([]);
      }
      const { setFieldValue } = formRef.current;

      for (const key in location.state) {
        if (Object.hasOwnProperty.call(location.state, key)) {
          const value = location.state[key];
          setFieldValue(key, value);
        }
      }
    }

    // return () => {};
  }, [location]);

  return (
    <GenericFormContainer
      cardHeaderTitle={cardHeaderTitle}
      navigateTo="/plant-master"
      p={2}
    >
      <MDBox p={2}>
        <GenericForm
          initialValues={formInitialValues}
          validationSchema={formValidationSchema}
          onSubmit={handleSubmit}
          validateOnChange
          innerRef={formRef}
          loadingStatus={
            (createPlantMutation.isLoading || updatePlantMutation.isLoading,
            isCompressing)
          }
          loadingText={
            createPlantMutation.isLoading
              ? "Creating..."
              : isCompressing
              ? "Compressing Image..."
              : "Updating..."
          }
        >
          {results
            .map((item) => item.isLoading)
            .every((item) => item === true) && (
            <Grid container spacing={2}>
              {Array.from({ length: 8 }).map((item) => (
                <Grid item lg={3} key={item}>
                  <Skeleton.Input active={true} block />
                </Grid>
              ))}

              <Grid item lg={12} md={12} xl={12}>
                <Grid
                  container
                  my={2}
                  spacing={2}
                  justifyContent="end"
                  alignItems="center"
                >
                  <Grid item lg={1} mr={4}>
                    <Skeleton.Button
                      active
                      block
                      shape={"square"}
                      size="large"
                    />
                  </Grid>
                  <Grid item lg={2}>
                    <Skeleton.Button
                      active
                      block
                      shape={"square"}
                      size="large"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          {results
            .map((item) => item.isSuccess)
            .every((item) => item === true) && (
            <>
              <Grid container spacing={2}>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <TextField
                    label="Plant Scientific Name"
                    name="Plant_Scientific_Name"
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <TextField label="Plant English Name" name="Plant_Eng_Name" />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <TextField
                    label="Plant Local Name"
                    name="Plant_Local_Name"
                    disabled={isPlantationUpdated}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <SelectField
                    label="Plant Habit"
                    name="Plant_Habit"
                    options={results
                      ?.map((item) => item?.data)
                      .map((item) => item.data)[0]
                      .map((item) => ({
                        value: item.Cat_Value,
                        label: item.Cat_Value,
                      }))}
                    placeholder="Select Options"
                    isRequired={true}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} my={2}>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <SelectField
                    label="Plant Nature"
                    name="Plant_Nature"
                    options={results
                      ?.map((item) => item?.data)
                      .map((item) => item.data)[1]
                      .map((item) => ({
                        value: item.Cat_Value,
                        label: item.Cat_Value,
                      }))}
                    placeholder="Select Options"
                    isRequired={true}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <SelectField
                    label="Plantation Type"
                    name="Plantation_Type"
                    options={results
                      ?.map((item) => item?.data)
                      .map((item) => item.data)[2]
                      .map((item) => ({
                        value: item.Cat_Value,
                        label: item.Cat_Value,
                      }))}
                    placeholder="Select Options"
                    isRequired={true}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <SelectField
                    label="Plant Season"
                    name="Plant_Season"
                    options={results
                      ?.map((item) => item?.data)
                      .map((item) => item.data)[3]
                      .map((item) => ({
                        value: item.Cat_Value,
                        label: item.Cat_Value,
                      }))}
                    placeholder="Select Options"
                    isRequired={true}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <TextField
                    label="Plant Uses"
                    name="Plant_Uses"
                    isRequired={true}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} my={2}>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <SelectField
                    label="Monitoring Frequency"
                    name="Monitoring_Frequency"
                    options={results
                      ?.map((item) => item?.data)
                      .map((item) => item.data)[5]
                      .map((item) => ({
                        value: item.Cat_Value,
                        label: item.Cat_Value,
                      }))}
                    placeholder="Select Options"
                    isRequired={true}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={4} xs={4}>
                  <TextField
                    label="Plant Description"
                    name="Plant_Desc"
                    onChange={(e) => {
                      setDescription(e.target.value);
                      formRef.current?.handleChange(e);
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="body2" color="textSecondary">
                            {`${description?.length || 0}/${MAX_CHARACTER_LENGTH}`}
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    // endAdornment={
                    //   <InputAdornment position="end">
                    //     <Typography.Text>{`${description?.length}/${MAX_CHARACTER_LENGTH}`}</Typography.Text>
                    //   </InputAdornment>
                    // }
                    inputProps={{ maxLength: MAX_CHARACTER_LENGTH }}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={4} xs={4}>
                  <ColorPicker
                    size="large" 
                    showText={(color) => <span>{getReadableColor(color.toHexString())} ({color.toHexString()})</span>} 
                    value={primary} 
                    onChangeComplete={(color) => setPrimary(color.toHexString())} 
                  />
                  {/* <MuiTextField
                    label="Plant Color"
                    name="Plant_Color"
                    value={primary}
                    isRequired={true}
                  /> */}
                   </Grid>
                <Grid
                  item
                  mb={matchesXs ? 2 : 0}
                  lg={3}
                  md={3}
                  sm={6}
                  xs={12}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FileField
                    name="Plant_Img"
                    label="Select File"
                    maxCount={1}
                    listType="picture"
                    fileList={fileList}
                    customRequest={({ file, onSuccess, onError }) => {
                      // You can perform any custom logic here
                      setTimeout(() => {
                        // Skip upload request since we're handling it manually
                        onSuccess();
                      }, 100);
                    }}
                    accept=".jpg,.png,.jpeg"
                    // showUploadList={false}
                    onChange={(info) => {
                      // This function is triggered when a file is selected
                      console.log(info);
                      if (info.file.status === "removed") {
                        formRef.current?.setFieldValue("Plant_Img", null);
                        setFileList([]);
                      } else {
                        setFileList(info.fileList);
                        // compressFile(info.file.originFileObj, 'Plant_Img', formRef);
                      }
                    }}
                  />
                </Grid>
              </Grid>
              <Grid
                container
                columnSpacing={{ xs: 1, sm: 5, md: 5, lg: 5 }}
                spacing={2}
                justifyContent="end"
                alignItems="center"
              >
                <Stack
                  direction={"row"}
                  spacing={2}
                  useFlexGap
                  alignItems="center"
                >
                  <MDButton
                    sx={{ px: 5 }}
                    variant="contained"
                    color="error"
                    size={matchesXs ? "small" : "large"}
                    onClick={() => navigate("/plant-master")}
                  >
                    Cancel
                  </MDButton>
                  {userHasRequiredRole && (
                    <MDButton
                      sx={{ px: 6 }}
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="success"
                      size={matchesXs ? "small" : "large"}
                      onClick={() => console.log("ON SUBMIT", formRef.current)}
                    >
                      {submitButtonLabel}
                    </MDButton>
                  )}
                </Stack>
              </Grid>
            </>
          )}
        </GenericForm>
      </MDBox>
    </GenericFormContainer>
  );
};

export default CreatePlant;
